import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Checkbox, Modal, Radio } from "antd";
import CommonStyles from "@components/CommonStyles";
import useToggle from "@base/hooks/useToggle";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { ISurveyAnswer, ISurveyQuestion } from "@base/modules/checkoutSurvey/checkoutSurvey.interface";
import checkoutSurveyService from "@base/modules/checkoutSurvey/checkoutSurveyService";
import {
  allowsDecimals,
  buildReviewItems,
  buildSurveyPayload,
  canProceed,
  getAnswerInput,
  getNextQuestionCode,
  isMultiple,
  ISurveyPage,
  mergeQuestion,
  sortByPosition,
} from "@base/modules/checkoutSurvey/checkoutSurveyRules";

interface IProps {
  open: boolean;
  onClose: () => void;
  surveyCode: string;
  /** Called with the finished `survey` string and its readable form; nothing is sent from here. */
  onCompleted: (survey: string, review: ReturnType<typeof buildReviewItems>) => void;
}

const codeOf = (answer: ISurveyAnswer) => (answer.answer_code ?? "").trim();

/** The checkout survey, one question per page. Finishing only builds the answers; the rating sends them. */
export default function CheckoutSurveyModal({ open, onClose, surveyCode, onCompleted }: IProps) {
  //! state
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const lang = i18n.resolvedLanguage ?? "vi";
  const { open: openConfirm, shouldRender: shouldRenderConfirm, toggle: toggleConfirm } = useToggle();

  const [groupCode, setGroupCode] = useState<string>(surveyCode);
  const [summaries, setSummaries] = useState<ISurveyQuestion[]>([]);
  const [pages, setPages] = useState<ISurveyPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // Questions already fetched, so going back and forth does not refetch them.
  const detailCache = useRef<Record<string, ISurveyQuestion>>({});

  const orderedCodes = summaries.map((question) => String(question.question_code));
  const current = pages[pages.length - 1];
  const nextCode = current ? getNextQuestionCode(current, orderedCodes) : undefined;
  const isLast = !!current && !nextCode;

  //! function
  const notifyError = useCallback(
    (description: string) => dispatch(setAlertMessage({ type: "error", message: t("Notification"), description })),
    [dispatch, t],
  );

  const loadQuestion = useCallback(
    async (questionCode: string, groupQuestions: ISurveyQuestion[]) => {
      if (detailCache.current[questionCode]) {
        return detailCache.current[questionCode];
      }

      const resp = await checkoutSurveyService.getQuestion(questionCode, lang);
      const merged = mergeQuestion(
        resp.data,
        groupQuestions.find((question) => question.question_code === questionCode),
      );
      detailCache.current[questionCode] = merged;

      return merged;
    },
    [lang],
  );

  const pushPage = useCallback((question: ISurveyQuestion) => setPages((list) => [...list, { question, selected: {}, typed: {} }]), []);

  // The parent's `onClose` is a new function on every render; read through a ref so it does not
  // restart the survey.
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const loadSurvey = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await checkoutSurveyService.getGroup(surveyCode, lang);
      const questions = sortByPosition(resp.data?.questions);

      if (!questions.length) {
        notifyError(t("checkoutSurvey.empty"));
        onCloseRef.current();
        return;
      }

      setGroupCode(resp.data?.group_code || surveyCode);
      setSummaries(questions);
      setPages([]);
      pushPage(await loadQuestion(String(questions[0].question_code), questions));
    } catch (error) {
      notifyError(t("checkoutSurvey.loadFailed"));
      onCloseRef.current();
    } finally {
      setIsLoading(false);
    }
  }, [surveyCode, lang, loadQuestion, notifyError, pushPage, t]);

  // Mounted only while open, so this runs once per opening (and again if the language changes).
  useEffect(() => {
    loadSurvey();
  }, [loadSurvey]);

  const updateCurrent = (update: (page: ISurveyPage) => ISurveyPage) => setPages((list) => [...list.slice(0, -1), update(list[list.length - 1])]);

  /** Picks or drops one answer. A single-answer question keeps only the one just picked. */
  const setChosen = (answer: ISurveyAnswer, chosen: boolean) =>
    updateCurrent((page) => {
      const code = codeOf(answer);
      const selected = isMultiple(page.question) || !chosen ? { ...page.selected } : {};

      selected[code] = chosen;

      return { ...page, selected };
    });

  /** Typing counts as choosing that answer; clearing the box un-chooses it. */
  const setTyped = (answer: ISurveyAnswer, value: string) =>
    updateCurrent((page) => {
      const code = codeOf(answer);
      const chosen = !!value.trim();
      const selected = isMultiple(page.question) || !chosen ? { ...page.selected } : {};

      selected[code] = chosen;

      return { ...page, selected, typed: { ...page.typed, [code]: value } };
    });

  const onNext = async () => {
    if (!current || !canProceed(current)) {
      return;
    }

    if (isLast) {
      toggleConfirm();
      return;
    }

    try {
      setIsLoading(true);
      pushPage(await loadQuestion(String(nextCode), summaries));
    } catch (error) {
      notifyError(t("checkoutSurvey.loadFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  // Going back keeps that page's answers; whatever came after it is dropped.
  const onBack = () => setPages((list) => (list.length > 1 ? list.slice(0, -1) : list));

  const onFinish = () => {
    toggleConfirm();

    const payload = buildSurveyPayload(groupCode, lang, pages);
    const review = buildReviewItems(
      payload,
      pages.map((page) => page.question),
    );

    dispatch(setAlertMessage({ type: "success", message: t("Notification"), description: t("checkoutSurvey.thanks") }));
    onCompleted(JSON.stringify(payload), review);
  };

  //! render
  const renderAnswer = (answer: ISurveyAnswer) => {
    const code = codeOf(answer);
    const kind = getAnswerInput(answer);
    const chosen = !!current?.selected[code];
    const typed = current?.typed[code] ?? "";
    const Choice = current && isMultiple(current.question) ? Checkbox : Radio;

    if (kind === "text" || kind === "number") {
      return (
        <CommonStyles.InputUI
          key={code}
          variant="filled"
          value={typed}
          placeholder={answer.answer_value}
          inputMode={kind === "number" ? "decimal" : undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            if (kind !== "number") {
              setTyped(answer, event.target.value);
              return;
            }

            // The backend only reads a dot as the decimal separator.
            const value = event.target.value.replace(/,/g, ".");
            const pattern = allowsDecimals(answer) ? /^\d*\.?\d*$/ : /^\d*$/;

            if (pattern.test(value)) {
              setTyped(answer, value);
            }
          }}
        />
      );
    }

    return (
      <div key={code} className="flex flex-col gap-2">
        <Choice checked={chosen} onChange={(event) => setChosen(answer, event.target.checked)}>
          {answer.answer_name}
        </Choice>

        {kind === "choiceWithText" ? (
          <CommonStyles.InputUI
            variant="filled"
            value={typed}
            placeholder={answer.answer_value}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setTyped(answer, event.target.value)}
          />
        ) : null}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      maskClosable={false}
      width={640}
      title={t("checkoutSurvey.title")}
      footer={
        <div className="flex justify-between gap-3">
          <CommonStyles.ButtonUI
            className="bg-common-primary200 text-common-primary hover:!bg-common-primary300 hover:!text-common-primary"
            disabled={pages.length <= 1 || isLoading}
            onClick={onBack}
          >
            {t("checkoutSurvey.back")}
          </CommonStyles.ButtonUI>

          <CommonStyles.ButtonUI type="primary" disabled={!current || !canProceed(current)} loading={isLoading} onClick={onNext}>
            {isLast ? t("checkoutSurvey.done") : t("checkoutSurvey.next")}
          </CommonStyles.ButtonUI>
        </div>
      }
    >
      <div className="relative min-h-[200px] flex flex-col gap-4">
        <CommonStyles.SpinnerUI isSpinning={isLoading} />

        {current ? (
          <>
            <div className="text-common-maintext text-base font-semibold font-['Inter']">
              {`${t("checkoutSurvey.question")} ${pages.length}: ${current.question.question_name ?? current.question.question_value ?? ""}`}
            </div>

            <div className="flex flex-col gap-3">{(current.question.answer ?? []).map(renderAnswer)}</div>
          </>
        ) : null}
      </div>

      {shouldRenderConfirm && (
        <CommonStyles.ModalConfirmationUI
          open={openConfirm}
          toggle={toggleConfirm}
          title={t("Notification")}
          content={t("checkoutSurvey.confirmFinish")}
          okText={t("Submit")}
          onConfirm={onFinish}
        />
      )}
    </Modal>
  );
}
